import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/**
 * @visibleName CustomSwitch component
 *
 * This component is a custom switch that allows the user to select between two options.
 *
 * @component
 * @example
 * return (
 *   <CustomSwitch
 *     option1={{ label: 'Option 1', value: 'option1' }}
 *     option2={{ label: 'Option 2', value: 'option2' }}
 *     onSelectSwitch={option => console.log(option)}
 *     value='option1'
 *   />
 * )
 */
const CustomSwitch = ({ option1, option2, onSelectSwitch, value, disabled, 'data-aid': dataAid }) => {
  const [selectedOption, setSelectedOption] = useState(value);

  const options = { [option1.value]: option1.label, [option2.value]: option2.label };

  const updateSwitchData = option => {
    if (disabled) {
      return;
    }

    setSelectedOption(option);
    onSelectSwitch(option);
  };

  return (
    <SwitchContainer data-aid={dataAid} disabled={disabled}>
      <Slider disabled={disabled} isSelectedOption1={selectedOption === option1.value}>
        {options[selectedOption]}
      </Slider>
      <OptionButton
        aria-pressed={selectedOption === option1.value}
        isSelected={selectedOption === option1.value}
        onClick={() => updateSwitchData(option1.value)}
      >
        {option1.label}
      </OptionButton>

      <OptionButton
        aria-pressed={selectedOption === option2.value}
        isSelected={selectedOption === option2.value}
        onClick={() => updateSwitchData(option2.value)}
      >
        {option2.label}
      </OptionButton>
    </SwitchContainer>
  );
};

CustomSwitch.propTypes = {
  option1: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  option2: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  onSelectSwitch: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const Slider = styled.div`
  align-items: center;
  background-color: ${p => (p.disabled ? p.theme.colors.gray400 : p.theme.colors.green400)};
  border-radius: 25px;
  color: white;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  transform: ${p => (p.isSelectedOption1 ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease;
  width: 50%;
`;

const OptionButton = styled.button.attrs(p => ({
  isSelected: p.isSelected || false,
}))`
  flex: 1;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  outline: none;
  background-color: white;
  color: ${p => p.theme.colors.gray700};
  transition: background-color 0.3s ease;
  border-radius: 25px;
`;

const SwitchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: white;
  border-radius: 25px;
  border-width: 1px;
  border-color: ${p => (!p.disabled ? p.theme.colors.gray700 : p.selectionColor)};
  position: relative;
  border: 1px solid ${p => (p.disabled ? p.theme.colors.gray100 : p.theme.colors.gray300)};
`;

export default CustomSwitch;
